import { lazy, useEffect } from 'react';
import { createBrowserRouter, Outlet, useLocation, useNavigate } from 'react-router-dom';

import RootProvider from 'watchtower-ui/contexts/Root';
import { t } from 'watchtower-ui/locales/i18n';
import Unauthorized from 'watchtower-ui/pages/Unauthorized/Unauthorized';
import { URL_PREFIX, US_REDIRECT_URL } from 'watchtower-ui/utils/Constants';

const SaturationCurvePage = lazy(
  () => import('watchtower-ui/pages/HistoricalPages/saturation_curve_v2/SaturationCurveV2'),
);

const DynamicDashboardPage = lazy(() => import('watchtower-ui/pages/HistoricalPages/DynamicDashboardPage'));
const HistoricalHome = lazy(() => import('watchtower-ui/pages/HistoricalPages/HistoricalHome'));
const HistoricalKpi = lazy(() => import('watchtower-ui/pages/HistoricalPages/kpi/HistoricalKpi'));
const HistoricalWaterfall = lazy(() => import('watchtower-ui/pages/HistoricalPages/waterfall/HistoricalWaterfall'));
const HistoricalDecomp = lazy(() => import('watchtower-ui/pages/HistoricalPages/decomp/HistoricalDecomp'));
const DriverAnalysis = lazy(() => import('watchtower-ui/pages/HistoricalPages/driver_analysis/DriverAnalysis'));
const ResourceAllocationLayout = lazy(() => import('watchtower-ui/pages/ResourceAllocation/Layout'));
const InputImpactInsights = lazy(() => import('watchtower-ui/pages/HistoricalPages/input_impact/InputImpactInsights'));
const ProjectList = lazy(() => import('watchtower-ui/pages/ResourceAllocation/ProjectList/Index'));
const PlanMeasurement = lazy(() => import('watchtower-ui/pages/ResourceAllocation/PlanMeasurement/Index'));
const NewPlan = lazy(() => import('watchtower-ui/pages/ResourceAllocation/NewPlan/Index'));
const Homepage = lazy(() => import('watchtower-ui/pages/Homepage/Homepage'));
// LandingPage kept for potential future use - now using CountrySelectionPage as default
// const LandingPage = lazy(() => import('watchtower-ui/pages/LandingPage/LandingPage'));
const CountrySelectionPage = lazy(
  () => import('watchtower-ui/pages/LandingPage/CountrySelection/CountrySelectionPage'),
);
const RequestAccessPage = lazy(() => import('watchtower-ui/pages/LandingPage/CountrySelection/RequestAccessPage'));
const DocumentationPage = lazy(() => import('watchtower-ui/pages/Documentation/DocumentationPage'));
const DataDictionaryPage = lazy(() => import('watchtower-ui/pages/Documentation/DataDictionary'));
const Profile = lazy(() => import('watchtower-ui/pages/Profile/Profile'));
const AdherenceTrackerLayout = lazy(() => import('watchtower-ui/pages/AdherenceTracker/AdherenceTrackerLayout'));
const AdherenceTrackerPage = lazy(() => import('watchtower-ui/pages/AdherenceTracker/AdherenceTrackerPage'));
const NotFound = lazy(() => import('watchtower-ui/components/common/404'));

const redirectWithQuery = (to: string) => {
  const Component = () => {
    const { search } = useLocation();
    const navigate = useNavigate();
    useEffect(() => navigate(`${to}${search}`, { replace: true }), [navigate, search]);
    return null;
  };
  return Component;
};

// US redirect component - redirects to US Watchtower without MSAL
const USRedirect = () => {
  useEffect(() => {
    globalThis.location.href = US_REDIRECT_URL;
  }, []);
  return null;
};

const router = createBrowserRouter([
  // Public routes - No RootProvider, no auth required
  {
    path: `${URL_PREFIX}/`,
    element: <Outlet />,
    children: [
      {
        index: true,
        Component: CountrySelectionPage, // Changed from LandingPage - public country selection
      },
      {
        path: 'select-country',
        Component: CountrySelectionPage, // Alias for backward compatibility
      },
    ],
  },
  // Request access page - no RootProvider needed, RequestAccessPage handles auth directly
  {
    path: `${URL_PREFIX}/request-access`,
    Component: RequestAccessPage,
  },
  // US redirect - public route, no MSAL, redirects to US Watchtower
  {
    path: `${URL_PREFIX}/us`,
    Component: USRedirect,
  },
  {
    path: `${URL_PREFIX}/us/*`,
    Component: USRedirect,
  },
  {
    path: `${URL_PREFIX}/:countryCode`,
    element: (
      <RootProvider>
        <Outlet />
      </RootProvider>
    ),
    children: [
      {
        index: true,
        Component: Homepage,
        handle: {
          label: 'Homepage',
        },
      },
      {
        path: 'data-dictionary',
        Component: DataDictionaryPage,
        handle: {
          label: 'Data Dictionary',
        },
      },
      {
        path: 'help/:path',
        Component: DocumentationPage,
        handle: {
          label: 'Documentation',
        },
      },
      {
        path: 'profile',
        Component: Profile,
        handle: {
          label: t('common:profile'),
        },
      },
      {
        path: 'historical',
        Component: DynamicDashboardPage,
        handle: {
          label: t('common:historicalHome'),
        },
        children: [
          {
            index: true,
            Component: HistoricalHome,
          },
          {
            path: 'waterfall',
            Component: HistoricalWaterfall,
            handle: {
              label: t('common:waterfall'),
            },
          },
          {
            path: 'kpi',
            Component: HistoricalKpi,
            handle: {
              label: t('common:spendEfficiencies'),
            },
          },
          {
            path: 'saturationcurve',
            Component: SaturationCurvePage,
            handle: {
              label: t('common:saturationCurve'),
            },
          },
          {
            path: 'decomp',
            Component: HistoricalDecomp,
            handle: {
              label: t('common:historicalDecomposition'),
            },
          },
          {
            path: 'roicurve',
            Component: redirectWithQuery('/optimization/plan'),
          },
          {
            path: 'input_impact',
            Component: redirectWithQuery('../input-impact'),
          },
          {
            path: 'input-impact',
            Component: InputImpactInsights,
            handle: {
              label: t('common:inputImpactInsights'),
            },
          },
          {
            path: 'driver-analysis',
            Component: DriverAnalysis,
            handle: {
              label: t('common:driverAnalysis'),
            },
          },
          {
            path: '*',
            Component: NotFound,
          },
        ],
      },
      {
        path: 'resource-allocation',
        handle: {
          label: t('resourceAllocation'),
        },
        Component: ResourceAllocationLayout,
        children: [
          {
            index: true,
            Component: ProjectList,
          },
          {
            path: 'plan-measurement',
            Component: redirectWithQuery('../plan-measurement/create'),
          },
          {
            path: 'plan-measurement/:id',
            handle: {
              label: t('pnl:planMeasurement'),
            },
            Component: PlanMeasurement,
          },
          {
            path: 'new-plan',
            Component: redirectWithQuery('../new-plan/create'),
          },
          {
            path: 'new-plan/:id',
            handle: {
              label: t('pnl:createNewPlan'),
            },
            Component: NewPlan,
          },
          {
            path: '*',
            Component: NotFound,
          },
        ],
      },
      {
        path: 'adherence',
        handle: {
          label: t('common:adherenceTracker'),
        },
        Component: AdherenceTrackerLayout,
        children: [
          {
            index: true,
            Component: AdherenceTrackerPage,
          },
          {
            path: '*',
            Component: NotFound,
          },
        ],
      },
      {
        path: 'unauthorized',
        Component: Unauthorized,
      },
      {
        path: '*',
        Component: NotFound,
      },
    ],
  },
  {
    path: '*',
    Component: NotFound,
  },
]);

export default router;
